import { gql } from "@apollo/client";
export const GET_VISA = gql`
  fragment getVisa on Visa {
    _id
    contractNumber
    visaNumber
    fullName
    fullNameAmharic
    cityAmharic
    passportNumber
    sponsorBirthDate
    wekalaDate
    visaQty
    salary
    currency
    remarks
    firstTime
    goodLooking
    writeRead
    profession
    religion
    ageCategory
    complexion
    status
    visaType
    address {
      telephone
      city
      country
    }
    owner {
      _id
      type
      displayName
      email
    }
  }
`;
/** Minimal visa fields for employee list, export, and embassy list PDF */
export const GET_VISA_ROW = gql`
  fragment getVisaRow on Visa {
    _id
    contractNumber
    visaNumber
    fullName
    fullNameAmharic
    passportNumber
    visaType
    address {
      telephone
      city
      country
    }
  }
`;

export const GET_VISA_NO_OWNER = gql`
  fragment getVisaNoOwner on Visa {
    _id
    contractNumber
    visaNumber
    fullName
    fullNameAmharic
    cityAmharic
    passportNumber
    sponsorBirthDate
    wekalaDate
    visaQty
    salary
    currency
    remarks
    firstTime
    goodLooking
    writeRead
    profession
    religion
    ageCategory
    complexion
    visaType
    status
    address {
      telephone
      city
      country
    }
    owner {
      _id
    }
  }
`;

export const GET_EMPLOYEE = gql`
  ${GET_VISA_NO_OWNER}
  fragment getEmployee on Employee {
    _id
    referenceNumber
    status
    currentStatusDate
    currentStatusRemark
    passportNumber
    placeOfBirth
    placeOfIssue
    notificationMessage
    medicalStatus
    medicalBookedDate
    medicalExpiryDate
    cocCompleted
    biometricsCompleted
    visaIssued
    visaIssuedDate
    labourIssued
    lmisIssuedDate
    ticketBooked
    givenName
    surName
    facePhoto
    standPhoto
    medicalPhoto
    passportScan
    originalIdScan
    emergencyContactIdScan
    insuranceScan
    cocCertificateScan
    educationCertificateScan
    fingerPrintScan
    employmentContractScan
    workingVisaScan
    employeeVideo
    sex
    dateOfBirth
    selectionDueDate
    passportIssueDate
    passportExpiryDate
    documents
    createdAt
    updatedAt
    additionalData {
      nationality
      pastNationality
      maritalStatus
      religion
      appliedProfession
      appliedCountry
      contractPeriod
      complexion
      documentReceivedDate
      salary
      currency
      numberOfChildren
      notes
      height
      weight
      fullNameAmharic
      lmisId
      labourId
      musanedHeld
      discontinuedDate
      discontinuedRemark
      afterFlightStatusDate
      afterFlightStatusRemark
    }
    address {
      telephone
      alternateTelephone
      region
      city
      streetAddress
      country
    }
    contactPerson {
      displayName
      kinship
      address {
        telephone
        alternateTelephone
        city
        streetAddress
        country
      }
    }
    education {
      arabicLanguage
      amharicLanguage
      cooking
      englishLanguage
      educationLevel
      skills
      fieldOfStudy
      yearCompleted
      educationQG
      remarks
    }
    embassyProcess {
      enjazNumber
      stampedDate
    }
    flight {
      flightDateTime
      arrivedDateTime
    }
    workExperience {
      _id
      country
      profession
      years
    }
    visa {
      ...getVisaNoOwner
    }
    owner {
      _id
      type
      displayName
      nameAmharic
      depositAmount
      email
      header
      remarks
      address {
        telephone
        city
        country
      }
    }
    partner {
      _id
      type
      displayName
      nameAmharic
      depositAmount
      email
      header
      remarks
      address {
        telephone
        city
        country
      }
    }
    parentCompany {
      _id
      type
      displayName
      depositAmount
      email
      header
      remarks
      address {
        telephone
        city
        country
      }
    }
    prospects {
      _id
      type
      displayName
      email
      remarks
      header
    }
  }
`;

/** Employee fields used by list UIs, Excel export, embassy list PDF, and status mutations */
export const GET_EMPLOYEE_ROW = gql`
  ${GET_VISA_ROW}
  fragment getEmployeeRow on Employee {
    _id
    referenceNumber
    status
    currentStatusDate
    currentStatusRemark
    medicalStatus
    medicalBookedDate
    medicalExpiryDate
    cocCompleted
    biometricsCompleted
    visaIssued
    visaIssuedDate
    labourIssued
    lmisIssuedDate
    ticketBooked
    flight {
      flightDateTime
      arrivedDateTime
    }
    givenName
    surName
    passportNumber
    facePhoto
    medicalPhoto
    employeeVideo
    sex
    placeOfBirth
    placeOfIssue
    dateOfBirth
    passportIssueDate
    passportExpiryDate
    createdAt
    additionalData {
      nationality
      pastNationality
      maritalStatus
      religion
      appliedProfession
      appliedCountry
      salary
      currency
      labourId
      musanedHeld
      fullNameAmharic
      discontinuedDate
      discontinuedRemark
      afterFlightStatusDate
      afterFlightStatusRemark
    }
    education {
      arabicLanguage
      englishLanguage
      yearCompleted
    }
    workExperience {
      _id
      country
      profession
      years
    }
    embassyProcess {
      enjazNumber
      stampedDate
    }
    address {
      telephone
      alternateTelephone
      region
      city
      streetAddress
      country
    }
    contactPerson {
      displayName
      kinship
      address {
        telephone
        alternateTelephone
        city
        streetAddress
        country
      }
    }
    visa {
      ...getVisaRow
    }
    owner {
      _id
      type
      displayName
      nameAmharic
    }
    partner {
      _id
      type
      displayName
      nameAmharic
    }
  }
`;

export const GET_EMPLOYEE_ONLY = gql`
  fragment getEmployeeOnly on Employee {
    _id
    referenceNumber
    status
    currentStatusDate
    currentStatusRemark
    passportNumber
    placeOfBirth
    placeOfIssue
    notificationMessage
    medicalStatus
    medicalBookedDate
    medicalExpiryDate
    cocCompleted
    biometricsCompleted
    visaIssued
    visaIssuedDate
    labourIssued
    lmisIssuedDate
    ticketBooked
    givenName
    surName
    facePhoto
    standPhoto
    medicalPhoto
    passportScan
    originalIdScan
    emergencyContactIdScan
    insuranceScan
    cocCertificateScan
    educationCertificateScan
    fingerPrintScan
    employmentContractScan
    workingVisaScan
    employeeVideo
    sex
    dateOfBirth
    selectionDueDate
    passportIssueDate
    passportExpiryDate
    documents
    additionalData {
      nationality
      pastNationality
      maritalStatus
      religion
      appliedProfession
      appliedCountry
      contractPeriod
      complexion
      documentReceivedDate
      salary
      currency
      numberOfChildren
      notes
      height
      weight
      musanedHeld
      discontinuedDate
      discontinuedRemark
      afterFlightStatusDate
      afterFlightStatusRemark
    }
    address {
      telephone
      alternateTelephone
      region
      city
      streetAddress
      country
    }
    contactPerson {
      displayName
      kinship
      address {
        telephone
        alternateTelephone
        city
        streetAddress
        country
      }
    }
    education {
      arabicLanguage
      amharicLanguage
      cooking
      englishLanguage
      educationLevel
      skills
      fieldOfStudy
      yearCompleted
      educationQG
      remarks
    }
    embassyProcess {
      enjazNumber
      stampedDate
    }
    flight {
      flightDateTime
      arrivedDateTime
    }
    workExperience {
      _id
      country
      profession
      years
    }

    owner {
      _id
      type
      displayName
      nameAmharic
      email
      remarks
      header
      address {
        telephone
        city
        country
      }
    }
    partner {
      _id
      type
      displayName
      nameAmharic
      email
      remarks
      header
      address {
        telephone
        city
        country
      }
    }
    parentCompany {
      _id
      type
      displayName
      email
      remarks
      header
      address {
        telephone
        city
        country
      }
    }
    prospects {
      _id
      type
      displayName
      email
      remarks
      header
    }
  }
`;
